/**
 * Topbar — Global search trigger + ET indicator + density/theme toggles +
 * notif bell + user menu.
 *
 * `onSearch` opens the command palette.
 */
import { useState } from 'react';
import {
  Search,
  Menu,
  Clock,
  Sun,
  Moon,
  Bell,
  LogOut,
  User as UserIcon,
  Settings,
  Rows3,
  AlignJustify,
  ChevronDown,
} from 'lucide-react';
import { NAV } from './nav';

function initials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export default function Topbar({
  user,
  role,
  theme,
  setTheme,
  density,
  setDensity,
  onMenu,
  onSearch,
  onNav,
  onLogout,
  notificationCounts = {},
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const totalNotif = Object.values(notificationCounts).reduce((a, b) => a + (b || 0), 0);

  // Build notif list from notificationCounts using NAV registry for labels/icons
  const notifItems = [];
  for (const sec of NAV) {
    for (const it of sec.items) {
      const n = it.badgeKey ? notificationCounts[it.badgeKey] : 0;
      if (n > 0) notifItems.push({ ...it, count: n });
    }
  }

  return (
    <header className="topbar" data-testid="lb-topbar">
      <button
        className="icon-btn hamburger"
        data-testid="menu-toggle"
        onClick={onMenu}
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>

      <button
        className="searchbar"
        data-testid="global-search"
        onClick={onSearch}
      >
        <Search size={16} />
        <span style={{ flex: 1, textAlign: 'left' }}>Search leads, pages, actions…</span>
        <span className="kbd desktop-only">⌘K</span>
      </button>

      <div style={{ flex: 1 }} />

      <span
        className="tz-pill desktop-only"
        data-testid="tz-indicator"
        title="All times shown in Eastern Time (ET)"
      >
        <Clock size={13} /> ET
      </span>

      <div
        className="seg desktop-only"
        data-testid="density-toggle"
        onClick={(ev) => {
          // If the click landed on a child <button> its handler already ran; otherwise treat
          // the wrapper as a one-step toggle so the testid works in click-the-control tests.
          if (ev.target.closest('button')) return;
          setDensity(density === 'comfortable' ? 'compact' : 'comfortable');
        }}
      >
        <button
          className={density === 'comfortable' ? 'on' : ''}
          onClick={() => setDensity('comfortable')}
          title="Comfortable"
          data-testid="density-comfortable"
        >
          <Rows3 size={14} />
        </button>
        <button
          className={density === 'compact' ? 'on' : ''}
          onClick={() => setDensity('compact')}
          title="Compact"
          data-testid="density-compact"
        >
          <AlignJustify size={14} />
        </button>
      </div>

      <button
        className="icon-btn"
        data-testid="theme-toggle"
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        title="Toggle theme"
      >
        {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
      </button>

      <div style={{ position: 'relative' }}>
        <button
          className="icon-btn"
          data-testid="notif-bell"
          title="Notifications"
          onClick={() => setNotifOpen((o) => !o)}
        >
          <Bell size={18} />
          {totalNotif > 0 && <span className="dot" />}
        </button>
        {notifOpen && (
          <>
            <div data-testid="topbar-div-2"
              style={{ position: 'fixed', inset: 0, zIndex: 50 }}
              onClick={() => setNotifOpen(false)}
            />
            <div className="notif-panel" data-testid="notif-panel">
              <div
                style={{
                  padding: '6px 10px 10px',
                  borderBottom: '1px solid var(--border)',
                  marginBottom: 6,
                  fontWeight: 800,
                  fontSize: 13.5,
                }}
              >
                Needs attention
              </div>
              {notifItems.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
                  All caught up.
                </div>
              ) : (
                notifItems.map((it) => {
                  const Icon = it.Icon;
                  return (
                    <button data-testid="topbar-button-3"
                      key={it.key}
                      className="notif-item"
                      style={{ width: '100%', border: 'none', textAlign: 'left' }}
                      onClick={() => {
                        onNav(it.key);
                        setNotifOpen(false);
                      }}
                    >
                      <span
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          background: 'var(--info-soft)',
                          color: 'var(--accent-deep)',
                          display: 'grid',
                          placeItems: 'center',
                        }}
                      >
                        <Icon size={15} />
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>{it.label}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                          {it.count} pending
                        </div>
                      </div>
                      <span
                        style={{
                          minWidth: 22,
                          padding: '0 7px',
                          height: 22,
                          borderRadius: 99,
                          background: 'var(--bad)',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 800,
                          display: 'grid',
                          placeItems: 'center',
                        }}
                      >
                        {it.count}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>

      <div style={{ position: 'relative' }}>
        <button
          data-testid="user-menu"
          onClick={() => setMenuOpen((m) => !m)}
          style={{ border: 'none', background: 'none', padding: 0, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        >
          <span className="avatar">{initials(user?.name || user?.email || 'User')}</span>
          <span className="desktop-only" style={{ textAlign: 'left', lineHeight: 1.15 }}>
            <div style={{ fontWeight: 800, fontSize: 13.5, color: 'var(--text)' }}>
              {(user?.name || user?.email || 'User').split(' ')[0]}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', fontWeight: 600 }}>
              {role === 'admin' ? 'Admin' : 'Team'}
            </div>
          </span>
          <ChevronDown
            size={14}
            className="desktop-only"
            style={{ color: 'var(--muted)' }}
          />
        </button>
        {menuOpen && (
          <>
            <div data-testid="topbar-div-1"
              style={{ position: 'fixed', inset: 0, zIndex: 50 }}
              onClick={() => setMenuOpen(false)}
            />
            <div className="user-menu">
              <div className="user-menu-head">
                <div style={{ fontWeight: 800, fontSize: 13.5, color: 'var(--text)' }}>
                  {user?.name || 'User'}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                  {user?.email || ''}
                </div>
              </div>
              <button data-testid="topbar-button-2" className="cmdk-item" style={{ width: '100%' }}>
                <span className="ci"><UserIcon size={16} /></span>Profile
              </button>
              <button data-testid="topbar-button-1" className="cmdk-item" style={{ width: '100%' }}>
                <span className="ci"><Settings size={16} /></span>Preferences
              </button>
              <button
                className="cmdk-item"
                data-testid="logout-button-v2"
                style={{ width: '100%', color: 'var(--bad)' }}
                onClick={onLogout}
              >
                <span className="ci" style={{ color: 'var(--bad)' }}><LogOut size={16} /></span>
                Log out
              </button>
            </div>
          </>
        )}
      </div>
    </header>
  );
}
